// Input validation for the kitchen API. Pure: each parser either returns a
// clean value or throws KitchenInputError with a message fit to show a user.

import { MEAL_SLOTS, type KitchenSettings, type MealInput, type MealSlot } from "./models";
import { MAX_TIMER_MS } from "./timers";

export class KitchenInputError extends Error {}

export const LIMITS = {
  mealTitle: 120,
  mealNotes: 500,
  url: 500,
  listName: 40,
  itemText: 120,
  itemQuantity: 30,
  timerLabel: 40,
  favorites: 200,
  lists: 20,
  itemsPerList: 300,
  rangeDays: 42,
} as const;

function fail(message: string): never {
  throw new KitchenInputError(message);
}

/** Strip control characters, collapse internal whitespace runs, trim, and cap length. */
export function cleanLine(v: unknown, max: number): string {
  if (typeof v !== "string") return "";
  return v
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

/** Like cleanLine but keeps line breaks (for notes). */
export function cleanBlock(v: unknown, max: number): string {
  if (typeof v !== "string") return "";
  return v
    .replace(/[\u0000-\u0009\u000b-\u001f\u007f]/g, " ")
    .replace(/\r\n?/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
    .slice(0, max);
}

export function isMealSlot(v: unknown): v is MealSlot {
  return typeof v === "string" && (MEAL_SLOTS as readonly string[]).includes(v);
}

export function isCivilDate(v: unknown): v is string {
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

/**
 * A recipe link must be an absolute http(s) URL with no credentials; returns
 * the normalized URL. A bare "example.com/recipe" gets https:// added.
 */
export function parseRecipeUrl(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v !== "string") fail("Recipe link must be text");
  const raw = v.trim();
  if (!raw) return null;
  if (raw.length > LIMITS.url) fail("Recipe link is too long");
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  let u: URL;
  try {
    u = new URL(withScheme);
  } catch {
    fail("That recipe link doesn’t look like a web address");
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") fail("Recipe links must start with http:// or https://");
  if (u.username || u.password) fail("Recipe links can’t include a username or password");
  if (!u.hostname.includes(".") && u.hostname !== "localhost") fail("That recipe link doesn’t look like a web address");
  return u.toString();
}

export function parseMealInput(raw: unknown): MealInput {
  if (!raw || typeof raw !== "object") fail("Expected a meal");
  const r = raw as Record<string, unknown>;
  const title = cleanLine(r.title, LIMITS.mealTitle);
  if (!title) fail("Give the meal a name");
  const notes = cleanBlock(r.notes, LIMITS.mealNotes) || null;
  const recipeUrl = parseRecipeUrl(r.recipeUrl);
  let cookId: string | null = null;
  if (r.cookId !== undefined && r.cookId !== null && r.cookId !== "") {
    if (typeof r.cookId !== "string" || r.cookId.length > 64) fail("Unknown household member");
    cookId = r.cookId;
  }
  return { title, notes, recipeUrl, cookId };
}

export function parseFavoriteInput(raw: unknown, partial: boolean): { title?: string; notes?: string | null; recipeUrl?: string | null } {
  if (!raw || typeof raw !== "object") fail("Expected a favorite");
  const r = raw as Record<string, unknown>;
  const out: { title?: string; notes?: string | null; recipeUrl?: string | null } = {};
  if (!partial || "title" in r) {
    out.title = cleanLine(r.title, LIMITS.mealTitle);
    if (!out.title) fail("Give the favorite a name");
  }
  if ("notes" in r) out.notes = cleanBlock(r.notes, LIMITS.mealNotes) || null;
  if ("recipeUrl" in r) out.recipeUrl = parseRecipeUrl(r.recipeUrl);
  return out;
}

export function parseListName(raw: unknown): string {
  const name = cleanLine(raw, LIMITS.listName);
  if (!name) fail("Give the list a name");
  return name;
}

export function parseItemInput(raw: unknown): { text: string; quantity: string | null } {
  if (!raw || typeof raw !== "object") fail("Expected an item");
  const r = raw as Record<string, unknown>;
  const text = cleanLine(r.text, LIMITS.itemText);
  if (!text) fail("Type something to add");
  const quantity = cleanLine(r.quantity, LIMITS.itemQuantity) || null;
  return { text, quantity };
}

/** Case- and space-insensitive key used to merge "Milk", " milk " and "MILK" in suggestions. */
export function itemKey(text: string): string {
  return text.toLocaleLowerCase("en-US").replace(/\s+/g, " ").trim();
}

export function parseTimerInput(raw: unknown): { label: string; durationMs: number } {
  if (!raw || typeof raw !== "object") fail("Expected a timer");
  const r = raw as Record<string, unknown>;
  const sec = r.durationSec;
  if (typeof sec !== "number" || !Number.isFinite(sec) || !Number.isInteger(sec)) fail("Timer length must be a whole number of seconds");
  if (sec < 1) fail("Timer must be at least 1 second");
  if (sec * 1000 > MAX_TIMER_MS) fail("Timers can run for up to 24 hours");
  const label = cleanLine(r.label, LIMITS.timerLabel) || "Timer";
  return { label, durationMs: sec * 1000 };
}

export function parseKitchenSettings(raw: unknown): Partial<KitchenSettings> {
  if (!raw || typeof raw !== "object") fail("Expected settings");
  const r = raw as Record<string, unknown>;
  const out: Partial<KitchenSettings> = {};
  if ("mealSlots" in r) {
    if (r.mealSlots !== "dinner" && r.mealSlots !== "all") fail("Meal slots must be “dinner” or “all”");
    out.mealSlots = r.mealSlots;
  }
  if ("weekStart" in r) {
    if (r.weekStart !== "monday" && r.weekStart !== "sunday") fail("Week start must be Monday or Sunday");
    out.weekStart = r.weekStart;
  }
  return out;
}

/** Validate a date range for the meal planner query. */
export function parseDateRange(start: unknown, end: unknown): { start: string; end: string } {
  if (!isCivilDate(start) || !isCivilDate(end)) fail("start and end must be YYYY-MM-DD dates");
  if (end < start) fail("end must not be before start");
  const days = (Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000;
  if (days > LIMITS.rangeDays) fail(`At most ${LIMITS.rangeDays} days at a time`);
  return { start, end };
}
